import { lazy } from 'react';
import type { JSX } from 'react';

import { BrowserRouter, Route, Routes } from 'react-router-dom';

import { getStorage } from 'forstok-ui-lib';

import PrivateRoute from './pages/users/partials/auth/privateRoute';
import { AndroidBackController } from './utils/android/androidBack';
import { PushNotificationsController } from './utils/android/pushNotificationsController';
import { isSalesAgentAccessible } from './helpers/share';

import type { TUser } from './types/share';

const ActivityIndex = lazy(() => import('./pages/activity'));
const ChatsIndex = lazy(() => import('./pages/chats'));
const ContactsDetail = lazy(() => import('./pages/contacts/details'));
const ContactsIndex = lazy(() => import('./pages/contacts'));
// const DashboardIndex = lazy(() => import('./pages/dashboard'));
const EmptyLayout = lazy(() => import('./layouts/empty'));
const ErrorsIndex = lazy(() => import('./pages/errors'));
const InvoicesDetail = lazy(() => import('./pages/invoices/detail'));
const InvoicesIndex = lazy(() => import('./pages/invoices'));
const InvoicesNew = lazy(() => import('./pages/invoices/new'));
const IntegrationsDetail = lazy(() => import('./pages/integrations/detail'));
const IntegrationsIndex = lazy(() => import('./pages/integrations'));
const IntegrationsSetup = lazy(() => import('./pages/integrations/setup'));
const InventoryDetail = lazy(() => import('./pages/inventory/detail'));
const InventoryIndex = lazy(() => import('./pages/inventory'));
const InventoryNew = lazy(() => import('./pages/inventory/new'));
const ItemsDetail = lazy(() => import('./pages/items/details'));
const ItemsIndex = lazy(() => import('./pages/items'));
const Layout = lazy(() => import('./layouts/layout'));
const PrintLayout = lazy(() => import('./layouts/print'));
const MarketingDetail = lazy(() => import('./pages/marketing/detail'));
const MarketingEdit = lazy(() => import('./pages/marketing/edit'));
const MarketingIndex = lazy(() => import('./pages/marketing'));
const MarketingNew = lazy(() => import('./pages/marketing/new'));
const OrdersDetail = lazy(() => import('./pages/orders/details'));
const OrdersEdit = lazy(() => import('./pages/orders/edit'));
const OrdersNew = lazy(() => import('./pages/orders/new'));
const OrdersIndex = lazy(() => import('./pages/orders'));
const PriceDetail = lazy(() => import('./pages/price/detail'));
const PriceIndex = lazy(() => import('./pages/price'));
const PriceNew = lazy(() => import('./pages/price/new'));
const ReportsDetail = lazy(() => import('./pages/reports/detail'));
const ReportsIndex = lazy(() => import('./pages/reports'));
const SalesAgentsDetail = lazy(() => import('./pages/salesAgents/detail'));
const SalesAgentsIndex = lazy(() => import('./pages/salesAgents'));
const SalesReturnNew = lazy(() => import('./pages/returns/new'));
const SettingsDetail = lazy(() => import('./pages/settings/detail'));
const SettingsIndex = lazy(() => import('./pages/settings'));
const StudioDetail = lazy(() => import('./pages/studio/detail'));
// const StudioEdit = lazy(() => import('./pages/studio/edit'));
const StudioIndex = lazy(() => import('./pages/studio'));
// const StudioNew = lazy(() => import('./pages/studio/new'));
const UsersIndex = lazy(() => import('./pages/users'));
const PrintIndex = lazy(() => import('./pages/prints'));

function App() {
  const users: TUser | undefined = getStorage('users', 'object');
  const evRoute = (path: string, element: JSX.Element, as?: string, layoutElement?: JSX.Element) => {
    const layout = layoutElement || <Layout />;
    return (
      <Route path={path} element={<PrivateRoute as={as} />}>
        <Route element={layout}>
          <Route index element={element} />
        </Route>
      </Route>
    );
  };

  return (
    <BrowserRouter>
      <AndroidBackController />
      <PushNotificationsController />
      <Routes>
        <Route path="/users" element={<EmptyLayout />}>
          <Route index element={<UsersIndex />} />
          <Route path="xyz" element={<UsersIndex />} />
          <Route path="forgot" element={<UsersIndex />} />
          <Route path="login" element={<UsersIndex />} />
          <Route path="verify" element={<UsersIndex />} />
        </Route>
        <Route path="/restricted" element={<ErrorsIndex section="restricted" />} />
        <Route path="/aistudio" element={<PrivateRoute as="chats" />}>
          <Route element={<Layout />}>
            <Route path="whatsAppForms">
              <Route path=":id" element={<StudioDetail sections="whatsAppForms" />} />
              <Route index element={<StudioIndex sections="whatsAppForms" />} />
            </Route>
            <Route path="reply">
              <Route path=":id" element={<StudioDetail sections="reply" />} />
              <Route index element={<StudioIndex sections="reply" />} />
            </Route>
          </Route>
        </Route>
        <Route path="/marketing" element={<PrivateRoute as="chats" />}>
          <Route element={<Layout />}>
            <Route path="whatsAppCampaigns">
              <Route path=":id" element={<MarketingDetail sections="campaign" />} />
              <Route path="edit/:id" element={<MarketingEdit sections="campaign" />} />
              <Route path="new" element={<MarketingNew sections="campaign" />} />
              <Route index element={<MarketingIndex sections="campaign" />} />
            </Route>
            <Route path="whatsAppTemplates">
              <Route path=":id" element={<MarketingDetail sections="template" />} />
              <Route path="new" element={<MarketingNew sections="template" />} />
              <Route index element={<MarketingIndex sections="template" />} />
            </Route>
            <Route path="vouchers">
              <Route path="shipping">
                <Route path=":id" element={<MarketingDetail sections="voucherShipping" />} />
                <Route path="edit/:id" element={<MarketingEdit sections="voucherShipping" />} />
                <Route path="new" element={<MarketingNew sections="voucherShipping" />} />
                <Route index element={<MarketingIndex sections="voucherShipping" />} />
              </Route>
              <Route path="product">
                <Route path=":id" element={<MarketingDetail sections="voucherProduct" />} />
                <Route path="edit/:id" element={<MarketingEdit sections="voucherProduct" />} />
                <Route path="new" element={<MarketingNew sections="voucherProduct" />} />
                <Route index element={<MarketingIndex sections="voucherProduct" />} />
              </Route>
              <Route path="promocode">
                <Route path=":id" element={<MarketingDetail sections="voucherPromoCode" />} />
                <Route path="edit/:id" element={<MarketingEdit sections="voucherPromoCode" />} />
                <Route path="new" element={<MarketingNew sections="voucherPromoCode" />} />
                <Route index element={<MarketingIndex sections="voucherPromoCode" />} />
              </Route>
              <Route path="uniquecode">
                <Route path=":id" element={<MarketingDetail sections="voucherUniqueCode" />} />
                <Route path="edit/:id" element={<MarketingEdit sections="voucherUniqueCode" />} />
                <Route path="new" element={<MarketingNew sections="voucherUniqueCode" />} />
                <Route index element={<MarketingIndex sections="voucherUniqueCode" />} />
              </Route>
            </Route>
          </Route>
        </Route>
        {isSalesAgentAccessible(users?.profile_id) ? (
          <Route path="/salesagents" element={<PrivateRoute as="chats" />}>
            <Route element={<Layout />}>
              <Route path="agents">
                <Route path=":id" element={<SalesAgentsDetail sections="agents" />} />
                <Route index element={<SalesAgentsIndex sections="agents" />} />
              </Route>
              <Route path="transactions">
                <Route path=":id" element={<SalesAgentsDetail sections="transactions" />} />
                <Route index element={<SalesAgentsIndex sections="transactions" />} />
              </Route>
              <Route path="payouts">
                <Route path=":id" element={<SalesAgentsDetail sections="payouts" />} />
                <Route index element={<SalesAgentsIndex sections="payouts" />} />
              </Route>
              {/* <Route path="settings" element={<SalesAgentsIndex sections="settings" />} /> */}
            </Route>
          </Route>
        ) : null}
        <Route path="/contacts" element={<PrivateRoute as="chats" />}>
          <Route element={<Layout />}>
            <Route path="customattribute" element={<ContactsIndex sections="customattribute" />} />
            <Route path="companies" element={<ContactsIndex sections="company" />} />
            <Route path=":id" element={<ContactsDetail sections="contact" />} />
            <Route path="companies/:id" element={<ContactsDetail sections="company" />} />
            <Route index element={<ContactsIndex />} />
          </Route>
        </Route>
        <Route path="/sales" element={<PrivateRoute as="chats" />}>
          <Route element={<Layout />}>
            <Route path="salesorders">
              <Route path="new" element={<OrdersNew />} />
              <Route path=":id" element={<OrdersDetail />} />
              <Route index element={<OrdersIndex />} />
            </Route>
            <Route path="quotations">
              <Route path="new/:id" element={<OrdersNew sections="quotation" />} />
              <Route path="new" element={<OrdersNew sections="quotation" />} />
              <Route path="edit/:id" element={<OrdersEdit sections="quotation" />} />
              <Route path=":id" element={<OrdersDetail sections="quotation" />} />
              <Route index element={<OrdersIndex sections="quotation" />} />
            </Route>
          </Route>
        </Route>
        <Route path="/items" element={<PrivateRoute as="chats" />}>
          <Route element={<Layout />}>
            <Route path="master">
              <Route path=":id" element={<ItemsDetail sections="master" />} />
              <Route index element={<ItemsIndex sections="master" />} />
            </Route>
            <Route path="listing">
              <Route path=":id" element={<ItemsIndex sections="listing" />} />
            </Route>
          </Route>
        </Route>
        <Route path="/inventory" element={<PrivateRoute as="chats" />}>
          <Route element={<Layout />}>
            <Route path="quantity">
              <Route index element={<InventoryIndex sections="quantity" />} />
            </Route>
            <Route path="adjustment">
              <Route index element={<InventoryIndex sections="adjustment" />} />
              <Route path="new" element={<InventoryNew sections="adjustment" />} />
              <Route path=":id" element={<InventoryDetail sections="adjustment" />} />
            </Route>
            <Route path="inbound">
              <Route index element={<InventoryIndex sections="inbound" />} />
              <Route path="new" element={<InventoryNew sections="inbound" />} />
              <Route path=":id" element={<InventoryDetail sections="inbound" />} />
            </Route>
            <Route path="outbound">
              <Route index element={<InventoryIndex sections="outbound" />} />
              <Route path="new" element={<InventoryNew sections="outbound" />} />
              <Route path=":id" element={<InventoryDetail sections="outbound" />} />
            </Route>
            <Route path="history">
              <Route index element={<InventoryIndex sections="history" />} />
            </Route>
          </Route>
        </Route>
        <Route path="/reports" element={<PrivateRoute as="chats" />}>
          <Route element={<Layout />}>
            <Route path="agents" element={<ReportsIndex sections="agents" />} />
            <Route path="CSAT">
              <Route path=":id/:name" element={<ReportsDetail sections="csat" />} />
              <Route index element={<ReportsIndex sections="csat" />} />
            </Route>
            <Route path="liveview" element={<ReportsIndex sections="liveview" />} />
            <Route path="conversations" element={<ReportsIndex sections="conversations" />} />
            <Route index element={<ReportsIndex />} />
          </Route>
        </Route>
        <Route path="/activity" element={<PrivateRoute as="chats" />}>
          <Route element={<Layout />}>
            <Route index element={<ActivityIndex sections="activitylogs" />} />
          </Route>
        </Route>
        <Route path="/settings" element={<PrivateRoute as="chats" />}>
          <Route element={<Layout />}>
            <Route path="agents">
              <Route path=":id/:channelId" element={<SettingsDetail sections="agentDetail" />} />
              <Route index element={<SettingsIndex sections="agents" />} />
            </Route>
            <Route path="labels" element={<SettingsIndex sections="labels" />} />
            <Route path="token" element={<SettingsIndex sections="token" />} />
            <Route index element={<SettingsIndex />} />
          </Route>
        </Route>
        <Route path="/integrations" element={<PrivateRoute as="chats" />}>
          <Route element={<Layout />}>
            <Route path="setup/:channel" element={<IntegrationsSetup />} />
            <Route path=":id/:channelId" element={<IntegrationsDetail sections="storesetting" />} />
            <Route index element={<IntegrationsIndex />} />
          </Route>
        </Route>
        {/* price page */}
        <Route path="/price" element={<PrivateRoute as="chats" />}>
          <Route element={<Layout />}>
            <Route path="priceadjustment">
              <Route path="new" element={<PriceNew sections="adjustment" />} />
              <Route path=":id" element={<PriceDetail sections="adjustment" />} />
              <Route index element={<PriceIndex sections="adjustment" />} />
            </Route>
            <Route path="pricehistory" element={<PriceIndex sections="history" />} />
          </Route>
        </Route>
        <Route path="/invoices" element={<PrivateRoute as="chats" />}>
          <Route element={<Layout />}>
            <Route path="salesinvoices">
              <Route path=":id" element={<InvoicesDetail sections="salesinvoices" />} />
              <Route index element={<InvoicesIndex sections="salesinvoices" />} />
            </Route>
            <Route path="paymentreceived">
              <Route path="new" element={<InvoicesNew sections="paymentreceived" />} />
              <Route path=":id" element={<InvoicesDetail sections="paymentreceived" />} />
              <Route index element={<InvoicesIndex sections="paymentreceived" />} />
            </Route>
          </Route>
        </Route>
        <Route path="/returns" element={<PrivateRoute as="chats" />}>
          <Route element={<Layout />}>
            <Route path="salesreturns">
              <Route path="new" element={<SalesReturnNew />} />
            </Route>
          </Route>
        </Route>
        <Route path="/deals" element={<PrivateRoute as="chats" />}>
          <Route element={<Layout />}>
            <Route index element={<ContactsIndex sections="deals" />} />
          </Route>
        </Route>
        <Route path="/tickets" element={<PrivateRoute as="chats" />}>
          <Route element={<Layout />}>
            <Route index element={<ContactsIndex sections="tickets" />} />
          </Route>
        </Route>
        <Route path="/prints" element={<PrivateRoute as="chats" />}>
          <Route element={<PrintLayout />}>
            <Route path="quotation/:id" element={<PrintIndex sections="quotation" />} />
          </Route>
        </Route>
        {evRoute('/', <ChatsIndex />, 'chats')}
        <Route path="*" element={<ErrorsIndex section="not-found" />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
